// ---------------------------------------------------------------------------
// CarPlayAnnouncement — one-time card about CarPlay, shown after the person
// has driven (PLE-13, Sep 30). It used to open 3s after the map on the very
// first launch, on top of everything else; see utils/carplayCardGate.
//
// NOT AN ANNOUNCEMENT ANY MORE (Morgan, Aug 30): "say we are on carplay,
// tonally as if it's not new." CarPlay shipped in 2.2 and reads as a standing
// feature now; a card that says "now on CarPlay" tells a new user they arrived
// late to someone else's launch. So it states the fact and gets on with the
// part that actually earns its place — the real problem it solves (Morgan:
// "it's hidden back on the last page of carplay — you have to go looking"):
// the drag-to-front fix, the Siri bypass, and the microphone prompt that iOS
// will only ever show on the phone.
//
// Shows once, dismiss forever.
// ---------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Car } from "lucide-react";
import { isCarplayConnected, primeVoicePermissions } from "@/native/carplayBridge";

const SEEN_KEY = "pleasant.carplayAnnounce.v1";

interface Props {
  /** The person has finished a drive or connected to CarPlay
   *  (isCarplayCardEligible / markCarplayCardEligible). */
  eligible: boolean;
  /** Something else owns the screen: navigation, the trip recap, or the
   *  trip-end sheet. The card never stacks on top of them. */
  busy: boolean;
}

export const CarPlayAnnouncement = ({ eligible, busy }: Props) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !eligible || busy) return;
    try {
      if (localStorage.getItem(SEEN_KEY) === "1") return;
    } catch {
      return;
    }
    // Wait for the screen to settle. After a drive the recap opens at 600ms
    // and the trip-end sheet at 2s; either flips `busy` and cancels this.
    const t = setTimeout(() => {
      // Connected to the car right now means they're probably driving: a
      // phone card plus mic prompts is the wrong thing to show. Not marked
      // seen, so the next calm launch shows it.
      if (isCarplayConnected()) return;
      setOpen(true);
    }, 3000);
    return () => clearTimeout(t);
  }, [eligible, busy]);

  if (!open) return null;

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* ok */ }
    setOpen(false);
    // ASK FOR THE VOICE PERMISSIONS HERE, while the phone is in their hand.
    //
    // iOS shows these prompts on the PHONE and never on the CarPlay screen,
    // so a driver who taps the car's mic first gets a button that appears to
    // do nothing — the alert is sitting on a phone in their pocket. (Morgan's
    // husband hit exactly this and only worked out what had happened later.)
    // Asking on dismiss means the prompt lands seconds after they've read the
    // words "voice search" above, and the car never has to ask at all.
    void primeVoicePermissions();
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-end sm:items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-3xl bg-card border border-border shadow-elevated p-6 pb-7">
        <div className="flex items-center gap-2 text-primary mb-3">
          <Car className="w-5 h-5" />
          <span className="wordmark text-3xl leading-none">Pleasant</span>
        </div>
        <h2 className="text-xl font-bold mb-2">Pleasant on CarPlay</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Plug your phone into your car and Pleasant is on the big screen —
          your three routes, voice search, stops along the way and one-tap road
          tagging, all from the dashboard.
        </p>
        <div className="rounded-2xl bg-muted/50 p-4 text-sm space-y-3 mb-5">
          <p>
            <span className="font-medium">Can&apos;t find it?</span> Apps land on the
            <em>last</em> page of your car screen. To move it up front: on your
            phone, open <span className="font-medium">Settings → General → CarPlay →
            your car</span>, then press and drag Pleasant where you want it.
          </p>
          <p>
            <span className="font-medium">Or skip the hunt:</span> say{" "}
            <em>&ldquo;Hey Siri, navigate with Pleasant.&rdquo;</em>
          </p>
          <p>
            <span className="font-medium">One thing first:</span> we&apos;ll ask
            for the microphone in a moment. Answering here means voice search
            just works in the car — your phone can only ask on this screen, never
            on the dashboard.
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="w-full h-12 rounded-full bg-pleasant text-primary-foreground font-semibold shadow-glow"
        >
          See you on the road
        </button>
      </div>
    </div>
  );
};
